import { useEffect, useState, useContext } from "react";
import SidebarField from "../../Components/SidebarField";
import SingleDropdown from "../../Components/DynamicForm/InputTypes/SingleDropdown";
import BasicTable from "../../Components/CommonTable/BasicTable/BasicTable";
import { useFormik } from "formik";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import preSuggestionApi from "../../App/api/preSuggestion.apis";
import LoaderContext from "../../Components/loader/LoaderContext";
import moment from "moment";
import { toast } from "react-toastify";

const userTypeOptions = [
  { name: "Vendor", code: "vendor" },
  { name: "Customer", code: "customer" },
];

const columns = [
  {
    field: "createdAt",
    header: "Date",
    accessor: (row) => moment(row.createdAt).format("DD MMM YYYY"),
  },
  { field: "user", header: "User Type" },
  { field: "message", header: "Description" },
  {
    field: "actions",
    header: "Actions",
    accessor: (row, handleCellClick) => (
      <div className="flex gap-2">
        <button
          className="btn d-flex align-items-center"
          style={{ color: "#fff" }}
          onClick={() => handleCellClick(row, "edit")}
          title="Edit"
        >
          <FaEdit size={18} />
        </button>
        <button
          className="btn d-flex align-items-center"
          style={{
            color: "#fff",
            background: "#e34e4e",
            borderColor: "#e34e4e",
          }}
          onClick={() => handleCellClick(row, "delete")}
          title="Delete"
        >
          <MdDelete size={20} />
        </button>
      </div>
    ),
  },
];

const searchBy = {
  filterType: "Search By",
  inputName: "search_by",
  options: [
    { name: "Vendor", code: "vendor" },
    { name: "Customer", code: "customer" },
    { name: "Description", code: "description" },
  ],
};

const filters = [
  {
    filterType: "User Type",
    inputName: "userType",
    options: userTypeOptions,
  },
];

const PreSuggestion = () => {
  const [showModal, setShowModal] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [error, setError] = useState(null);
  const { handleLoading } = useContext(LoaderContext);
  // Pagination state
  const [pagination, setPagination] = useState({ page: 1, limit: 5 });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      handleLoading(true);
      setError(null);
      try {
        const res = await preSuggestionApi.getAllPreSuggestion(pagination);
        // API should return suggestions, totalPages, totalCount
        setTableData(res.data?.data?.suggestions || res.data?.data || []);
        setTotalPages(res.data?.data?.totalPages || 1);
        setTotalCount(res.data?.data?.totalCount || 0);
      } catch (err) {
        setError("Failed to fetch suggestions");
      } finally {
        handleLoading(false);
      }
    };
    fetchData();
  }, [pagination]);

  const formik = useFormik({
    initialValues: {
      user: "",
      message: "",
    },
    onSubmit: async (values, { resetForm }) => {
      handleLoading(true);
      setError(null);
      try {
        if (editRow) {
          await preSuggestionApi.editPreSuggestion(editRow._id, {
            user: values.user,
            message: values.message,
          });
          toast.success("Message set edit successfully");
        } else {
          await preSuggestionApi.addPreSuggestion({
            user: values.user,
            message: values.message,
          });
          toast.success("Message set successfully");
        }
        const res = await preSuggestionApi.getAllPreSuggestion();
        setTableData(res.data?.data?.suggestions || []);
        setTotalPages(res.data?.data?.totalPages || 1);
        setTotalCount(res.data?.data?.totalCount || 0);
        resetForm();
        setShowModal(false);
        setEditRow(null);
      } catch (err) {
        setError("Failed to save suggestion");
      } finally {
        handleLoading(false);
      }
    },
  });

  const handleCellClick = async (row, action) => {
    if (action === "edit") {
      setEditRow(row);
      formik.setValues({
        user: row.user || "",
        message: row.message,
      });
      setShowModal(true);
    } else if (action === "delete") {
      handleLoading(true);
      setError(null);
      try {
        await preSuggestionApi.deletePreSuggestion(row._id, {});
        toast.success("Message set deleted successfully");
        const res = await preSuggestionApi.getAllPreSuggestion();
        setTableData(res.data?.data?.suggestions || []);
        setTotalPages(res.data?.data?.totalPages || 1);
        setTotalCount(res.data?.data?.totalCount || 0);
      } catch (err) {
        setError("Failed to delete suggestion");
      } finally {
        handleLoading(false);
      }
    }
  };

  const handleCreate = () => {
    setEditRow(null);
    formik.resetForm();
    setShowModal(true);
  };

  const onPaginatorClick = (e) => {
    if (e) {
      setPagination({
        limit: e.rows,
        page: +e.page + 1,
      });
    }
  };

  return (
    <div className="main-content max-w-7xl mx-auto">
      <div className="d-flex justify-content-between align-items-center mb-4 gap-1">
        <h2 className="text-3xl font-bold" style={{ color: "var(--color--2)" }}>
          Pre Suggestions
        </h2>
        <button
          className="btn btn-primary d-flex align-items-center gap-1"
          onClick={handleCreate}
          style={{ color: "#fff" }}
        >
          <span className="pi pi-plus" style={{ fontSize: "1.2rem" }} />
          Create Suggestions
        </button>
      </div>
      {error && <div className="alert alert-danger mb-2">{error}</div>}
      <div className="">
        <BasicTable
          columns={columns}
          data={tableData}
          SearchBy={searchBy}
          filters={filters}
          handleCellClick={handleCellClick}
          onPaginatorClick={onPaginatorClick}
          paginatorData={{
            page: pagination.page,
            limit: pagination.limit,
            totalPages: totalPages,
            totalCount: totalCount,
          }}
        />
      </div>
      {showModal && (
        <SidebarField
          title={editRow ? "Edit Pre Suggestion" : "Create Pre Suggestion"}
          handleClose={() => {
            setShowModal(false);
            setEditRow(null);
            formik.resetForm();
          }}
          button0={
            <button
              className="btn btn-primary w-full"
              onClick={formik.handleSubmit}
              disabled={!formik.values.user || !formik.values.message}
              type="button"
              style={{ color: "#fff" }}
            >
              Save
            </button>
          }
          button1={
            <button
              className="btn btn-secondary w-full"
              onClick={() => {
                setShowModal(false);
                setEditRow(null);
                formik.resetForm();
              }}
              type="button"
              style={{
                color: "#fff",
                background: "#e34e4e",
                borderColor: "#e34e4e",
              }}
            >
              Cancel
            </button>
          }
        >
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-col gap-4 mt-2"
          >
            <div>
              <label className="form-label mb-1">User Type</label>
              <SingleDropdown
                inputName="user"
                placeholder="Select User Type"
                options={userTypeOptions}
                optionLabel="name"
                optionValue="code"
                value={formik.values.user}
                handleFilter={formik.setFieldValue}
                sx={{
                  border: "1.5px solid var(--color--2)",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              />
            </div>
            <div>
              <label className="form-label mb-1">Description</label>
              <textarea
                name="message"
                placeholder="Description"
                className="form-input min-h-[100px]"
                value={formik.values.message}
                onChange={formik.handleChange}
              />
            </div>
          </form>
        </SidebarField>
      )}
    </div>
  );
};

export default PreSuggestion;
